import { useState, useCallback, useEffect, useRef, useMemo } from 'react';

import { usePersonalisationContext } from '../../context/PersonalisationContext';
import { useLocale } from '../../context/LocaleContext';
import {
  validateStep,
  isStepRequired,
  isOptionalUvInsertComplete,
  validateSlotSelection,
  getStepSlotIdsForValidation
} from '../../utilities/stepValidation';
import { logError } from '../../utilities/logging';
import { generatePersonalisationUrl } from '../../utilities/urlSharing';

import styles from './personalisation-footer.module.css';

/**
 * Determines the CTA button text based on step state.
 * Extracted as a pure function for testability.
 * @param {Object} options
 * @param {boolean} options.isStepRequired - Whether the step has required slots or minRequired > 0.
 * @param {boolean} options.isLastStep - Whether this is the final step.
 * @param {boolean} options.hasSelectionsOnCurrentStep - Whether the user has made any selections.
 * @param {boolean} options.isPageContentSlot - Whether the step contains PAGE_CONTENT slots.
 * @param {boolean} options.hasInvalidSelections - Whether there are invalid selections that need review.
 * @returns {string} The button text.
 */
export function determineButtonText({
  isStepRequired,
  isLastStep,
  hasSelectionsOnCurrentStep,
  isPageContentSlot,
  hasInvalidSelections
}) {
  // Handle invalid selections on the last step.
  if (isLastStep && hasInvalidSelections) {
    return 'Review selections';
  }
  if (isStepRequired) {
    return isLastStep ? 'Add to bag' : 'Continue';
  } else {
    if (hasSelectionsOnCurrentStep) {
      return isLastStep ? 'Add to bag' : 'Continue';
    } else {
      if (isPageContentSlot) {
        return isLastStep ? 'Add to bag' : 'Continue';
      }
      return isLastStep ? 'Skip & add to bag' : 'Skip & Continue';
    }
  }
}

/**
 * Custom hook for PersonalisationFooter logic and state management.
 * @param {Object} props - Component props.
 * @returns {Object} View model for the footer component.
 */
export function useViewModel(props) {
  const {
    currentTotalPrice,
    updateTheCart,
    personalisationSteps,
    personalisationSlots,
    selections,
    updateSelections,
    isAddingToCart,
    hasPersonalisationOptions,
    product,
    isSharedPersonalisation,
    variants,
    resolvedUvInserts
  } = usePersonalisationContext();
  const { formatPrice, country } = useLocale();
  const { currentStep, totalSteps, onPrev, onNext, onClose } = props;

  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState('success');
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const hasAutoOpenedSummaryRef = useRef(false);

  // Get current step for validation.
  const stepIds = Array.from(personalisationSteps?.keys() || []);
  const currentStepId = stepIds[`${currentStep}`];
  const currentStepData = personalisationSteps?.get(currentStepId);

  // Validate based on whether there are personalisation options.
  let isStepValid;
  let stepValidation;
  if (!hasPersonalisationOptions) {
    // If no personalisation options, check if the base product is available for sale.
    isStepValid = product?.available === true;
  } else {
    // If there are personalisation options, validate the current step.
    stepValidation = validateStep({
      step: currentStepData,
      personalisationSlots: personalisationSlots || new Map(),
      selections: selections?.slots || []
    });
    isStepValid = stepValidation.isValid;
  }

  const isOptionalUvInsertCompleteValue =
    hasPersonalisationOptions && currentStepData
      ? isOptionalUvInsertComplete({
          step: currentStepData,
          personalisationSlots: personalisationSlots || new Map(),
          selections: selections?.slots || [],
          resolvedUvInserts
        })
      : true;

  if (isStepValid && !isOptionalUvInsertCompleteValue) {
    isStepValid = false;
  }

  // Determine if the current step is required using the utility function.
  const isStepRequiredValue =
    hasPersonalisationOptions && currentStepData
      ? isStepRequired({
          step: currentStepData,
          personalisationSlots: personalisationSlots || new Map()
        })
      : false;

  const hasValidSelectionForSlot = (slotId) => {
    const slot = personalisationSlots?.get(slotId);
    if (!slot) return false;

    const selection = selections?.slots?.find((selectionItem) => selectionItem.id === slotId);
    return validateSlotSelection({
      slot,
      selectedValue: selection?.selected,
      resolvedUvInserts
    });
  };

  // Slot IDs that reflect the customer's current interaction on this step.
  // For GROUPED steps, only the active option group's slots count (inactive groups are ignored).
  const slotIdsForStepInteraction =
    hasPersonalisationOptions && currentStepData?.slots
      ? currentStepData.slotMode === 'GROUPED'
        ? getStepSlotIdsForValidation({
            step: currentStepData,
            personalisationSlots: personalisationSlots || new Map(),
            selections: selections?.slots || []
          })
        : currentStepData.slots
      : [];

  // Check if user has made any selections on the current step (optional steps use this for CTA label).
  let hasSelectionsOnCurrentStep = false;
  if (slotIdsForStepInteraction.length > 0) {
    hasSelectionsOnCurrentStep = slotIdsForStepInteraction.some((slotId) => hasValidSelectionForSlot(slotId));
  }

  // Only toastClassName has logic.
  const toastClassName = `${styles[`personalisation__footer-toast`]} ${styles[`personalisation__footer-toast--${toastType}`]}`;

  // Handlers
  const handleShareDesign = useCallback(() => {
    if (navigator.clipboard && window.isSecureContext) {
      // Generate URL with personalisation data
      const shareUrl = generatePersonalisationUrl({
        baseUrl: window.location.href,
        selections
      });

      navigator.clipboard
        .writeText(shareUrl)
        .then(() => {
          setToastType('success');
          setShowToast(true);
          setTimeout(() => setShowToast(false), 3000);
        })
        .catch((err) => {
          setToastType('error');
          setShowToast(true);
          setTimeout(() => setShowToast(false), 3000);
          logError({
            label: 'useViewModel',
            error: err,
            message: 'Failed to copy URL to clipboard',
            extraData: { url: shareUrl }
          });
        });
    }
  }, [selections]);

  const handleSummaryModalOpen = useCallback(() => {
    setSummaryModalOpen(true);
  }, []);

  const handleSummaryModalClose = useCallback(() => {
    setSummaryModalOpen(false);
  }, []);

  // Auto-open summary modal once when arriving via shared personalisation.
  // Skip auto-open if visualiser is enabled to avoid blocking the visualiser view.
  useEffect(() => {
    // @ts-expect-error - window.personalisation is added at runtime.
    const isVisualiserEnabled = window.personalisation?.visEnabled === 'true';
    if (isSharedPersonalisation && !hasAutoOpenedSummaryRef.current && !isVisualiserEnabled) {
      hasAutoOpenedSummaryRef.current = true;
      setSummaryModalOpen(true);
    }
  }, [isSharedPersonalisation]);

  // Compute invalid selections (shared journeys only) to guard ATB.
  const invalidSelectionSlotIds = useMemo(() => {
    if (!isSharedPersonalisation) return [];
    if (!selections || !selections.slots) return [];
    const invalidIds = [];
    selections.slots.forEach(({ id: slotId, selected }) => {
      const slot = personalisationSlots?.get(slotId);
      if (!slot) return;
      if (slot.slotType !== 'COMPONENT' && slot.slotType !== 'BASE_CONFIG') return;
      const values = Array.isArray(selected) ? selected : [selected];
      const hasInvalid = values.some((val) => {
        if (!val) return false;
        const v = variants.get(val);
        if (!v) return true;
        return v.availableForSale === false;
      });
      if (hasInvalid) invalidIds.push(slotId);
    });
    return invalidIds;
  }, [isSharedPersonalisation, selections, personalisationSlots, variants]);

  const hasInvalidSelections = invalidSelectionSlotIds.length > 0;

  const handlePrev = useCallback(() => {
    if (onPrev) onPrev();
  }, [onPrev]);

  const handleNext = useCallback(async () => {
    // Check if current step has location map slots that need image generation
    if (currentStepData?.slots) {
      const locationMapSlots = currentStepData.slots
        .map((slotId) => personalisationSlots?.get(slotId))
        .filter((slot) => {
          if (!slot) return false;
          const isLocationMap = slot.slotType === 'LOCATION_MAP';
          if (!isLocationMap) return false;

          // Always regenerate the B&W image on "Continue" so map repositioning is captured.
          const selection = selections?.slots?.find((s) => s.id === slot.id);
          if (!selection?.selected) return false;

          // eslint-disable-next-line -- selected can be string or object depending on slot state.
          const selectedData =
            typeof selection.selected === 'string'
              ? (() => {
                  try {
                    return JSON.parse(selection.selected);
                  } catch {
                    return null;
                  }
                })()
              : selection.selected;

          if (!selectedData) return false;
          return Boolean(selectedData.locationPlace);
        });

      // Generate images for any location map slots that need it.
      if (locationMapSlots.length > 0) {
        // Clear any saved IMAGE_UPLOAD selection so the next-step editor auto-opens
        // with the newly generated map instead of preserving the previous edit.
        const imageUploadSelections =
          selections?.slots?.filter((selection) => selection.slotType === 'IMAGE_UPLOAD' && selection.selected) || [];

        await Promise.all(
          imageUploadSelections.map((selection) =>
            updateSelections({
              slotId: selection.id,
              itemSelected: null
            })
          )
        );

        const generationPromises = locationMapSlots.map(async (slot) => {
          const refElement = document.querySelector(`[data-location-map-generator-ref="${slot.id}"]`);
          // @ts-expect-error - Accessing stored ref.
          const refObject = refElement?._generatorRef;
          const generatorRef = refObject?.current;
          if (generatorRef && typeof generatorRef.generateMapImage === 'function') {
            try {
              await generatorRef.generateMapImage();
            } catch (error) {
              console.error('Failed to generate location map image:', error);
            }
          }
        });

        await Promise.all(generationPromises);
      }
    }

    if (onNext) onNext();
  }, [onNext, currentStepData, personalisationSlots, selections, updateSelections]);

  const finishPersonalisation = useCallback(async () => {
    // If invalid selections exist, show summary for correction and block ATB.
    if (hasInvalidSelections) {
      setSummaryModalOpen(true);
      return;
    }
    await updateTheCart({
      country,
      onSuccess: onClose
    });
  }, [updateTheCart, country, onClose, hasInvalidSelections]);

  const isLastStep = currentStep === totalSteps - 1;

  const getButtonText = useCallback(() => {
    // Check if current step contains PAGE_CONTENT slotType.
    const isPageContentSlot = currentStepData?.slots?.some((slotId) => {
      const slot = personalisationSlots?.get(slotId);
      return slot?.slotType === 'PAGE_CONTENT';
    });

    return determineButtonText({
      isStepRequired: isStepRequiredValue,
      isLastStep,
      hasSelectionsOnCurrentStep,
      isPageContentSlot,
      hasInvalidSelections
    });
  }, [
    isStepRequiredValue,
    isLastStep,
    hasSelectionsOnCurrentStep,
    currentStepData,
    personalisationSlots,
    hasInvalidSelections
  ]);

  return {
    toastClassName,
    showToast,
    toastType,
    handleShareDesign,
    handleSummaryModalOpen,
    handleSummaryModalClose,
    summaryModalOpen,
    handlePrev,
    handleNext,
    finishPersonalisation,
    currentStep,
    totalSteps,
    currentTotalPrice,
    isLastStep,
    isStepValid,
    isStepRequired: isStepRequiredValue,
    hasSelectionsOnCurrentStep,
    isAddingToCart,
    formatPrice,
    getButtonText
  };
}
